import { createFileRoute } from "@tanstack/react-router";
import { MarketingShell } from "@/components/marketing-shell";
import { redirectIfAuthenticated } from "@/lib/marketing-guards";

export const Route = createFileRoute("/terms")({
  beforeLoad: redirectIfAuthenticated,
  head: () => ({
    meta: [
      { title: "Terms of Service, Super Affiliate OS" },
      {
        name: "description",
        content:
          "The terms and conditions governing your use of Super Affiliate OS, including account, billing, acceptable use, and liability provisions.",
      },
      { property: "og:title", content: "Terms of Service, Super Affiliate OS" },
    ],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <MarketingShell>
      <article className="prose prose-invert mx-auto max-w-3xl px-6 py-20 prose-headings:tracking-tight prose-h1:text-4xl prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-3 prose-p:text-muted-foreground prose-li:text-muted-foreground prose-strong:text-foreground">
        <h1>Terms of Service</h1>
        <p className="text-sm text-muted-foreground">Last updated: May 19, 2026</p>

        <p>
          These Terms of Service ("Terms") govern your access to and use of Super Affiliate OS
          (the "Service"). By creating an account or using the Service, you agree to these Terms.
        </p>

        <h2>1. Your account</h2>
        <p>
          You must be at least 18 years old and provide accurate information when you sign up.
          You're responsible for keeping your credentials safe and for everything that happens
          under your account. Notify us immediately if you suspect unauthorized access.
        </p>

        <h2>2. Subscriptions, billing, and trials</h2>
        <p>
          Paid plans are billed in advance, monthly or annually, via Stripe. Every plan starts
          with a 14-day free trial. You won't be charged until the trial ends, and you can
          cancel any time from Settings → Billing. Annual plans are non-refundable after the
          trial ends; monthly plans can be cancelled effective at the end of the current period.
        </p>

        <h2>3. Acceptable use</h2>
        <ul>
          <li>Don't upload data you don't have the right to use.</li>
          <li>Don't attempt to reverse engineer, scrape, or disrupt the Service.</li>
          <li>Don't use the Service for unlawful, fraudulent, or harmful activity.</li>
          <li>Don't share your account credentials. Use teammate seats instead.</li>
        </ul>

        <h2>4. Your data</h2>
        <p>
          You own your data. We process it only to provide the Service to you, and we never
          sell it. You can export every workspace's commissions, contacts, and imports at any
          time. If you cancel, we retain your data for 30 days in case you reactivate, then
          delete it.
        </p>

        <h2>5. Intellectual property</h2>
        <p>
          We own the Service, the software, the brand, and the underlying intellectual
          property. You retain ownership of your data and any content you upload.
        </p>

        <h2>6. Service availability</h2>
        <p>
          We aim for high availability but don't guarantee uninterrupted service. We may
          perform maintenance, deploy updates, or change features without notice.
        </p>

        <h2>7. Disclaimers and liability</h2>
        <p>
          The Service is provided "as is" without warranties of any kind. To the maximum
          extent permitted by law, our total liability for any claim is limited to the
          amount you paid us in the 12 months preceding the claim.
        </p>

        <h2>8. Termination</h2>
        <p>
          You may close your account at any time. We may suspend or terminate accounts that
          violate these Terms or fail to pay. On termination, your right to use the Service
          ends and we follow the data retention policy above.
        </p>

        <h2>9. Changes to these Terms</h2>
        <p>
          We may update these Terms occasionally. Material changes will be announced by email
          or in-app notice. Continued use after a change means you accept the new Terms.
        </p>

        <h2>10. Contact</h2>
        <p>
          Questions about these Terms? Email <strong>support@superaffiliateos.com</strong>.
        </p>
      </article>
    </MarketingShell>
  );
}
